import { useCallback, useEffect, useState } from "react";
import dotsImage from "@/assets/images/dots.png";
import { Button } from "@/common/components/button";
import {
  SlidderWrapper,
  NewRegWrapper,
  Slide,
  Wrapper,
  RedirectModalWrapper,
} from "./styles";
import OTPVerification from "./onboarding/otp-verification";
import { FormContainer } from "./styles";
import UserDetail, { IStep1Data, ICluster } from "./onboarding/user-details";
import TinVerification from "./onboarding/tin_verification";
import PcnIDVerification from "./onboarding/pcn-lincence";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import {
  COMPANY_URL,
  REGISTER_USER_URL,
  ALL_CLUSTERS,
  CLUSTER_REDIRECTS,
} from "@/common/constants";
import { Modal } from "@/common/components/modal";
import { useNotification } from "@/common/components/Notifications/NotificationProvider";
import useQuery from "@/hooks/useQuery";
import ClusterSelectionModal from "./onboarding/clusterSelectionModal";

// ─── Types ────────────────────────────────────────────────────────────────────
export interface ILoginProps {}

const initialFormData: IStep1Data = {
  email: "",
  phone_number: undefined,
  company_type_id: "",
  product_type_id: "",
  company_name: "",
  market_cluster: "",
};

const initialPCNDetails = {
  pcn_licence_certificate: null as File | null,
  current_pcn_licence_number: "",
  current_pcn_licence_iss_date: "",
  current_pcn_licence_exp_date: "",
  hasPcn: "no",
};

// ─── Main Component ───────────────────────────────────────────────────────────
export function NewReg(_: ILoginProps) {
  const [formData, setFormData] = useState<IStep1Data>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [tinData, setTinData] = useState<any>({});
  const [companyData, setCompanyData] = useState<any>({});
  const [pcnData, setPcnData] = useState(initialPCNDetails);
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [showRedirectModal, setShowRedirectModal] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState("");

  // ── Cluster state ──────────────────────────────────────────────────────
  // null = modal open; ICluster = confirmed selection, form is unlocked
  // Read persisted cluster from sessionStorage so the modal doesn't reappear
  // when the URL changes to ?currentStep=2, 3, 4 within the same session.
  const [selectedCluster, setSelectedCluster] = useState<ICluster | null>(
    () => {
      try {
        const saved = sessionStorage.getItem("selectedCluster");
        return saved ? (JSON.parse(saved) as ICluster) : null;
      } catch {
        return null;
      }
    },
  );

  // Fetch clusters exactly as UserDetail does
  const {
    loading: clustersLoading,
    data: clustersData,
    error: clustersError,
  } = useQuery<ICluster[]>(ALL_CLUSTERS);

  const currentStep = parseInt(searchParams.get("currentStep") || "1");
  const phoneNumber = searchParams.get("phone_number");
  const verificationCode = searchParams.get("verification_code");

  const makeRequest = useApiCallHandling();
  const notify = useNotification();
  const navigate = useNavigate();

  useEffect(() => {
    const compData = JSON.parse(localStorage.getItem("companyData") || "{}");
    setCompanyData(compData);
  }, [setTinData]);

  // ── Handle cluster selection ───────────────────────────────────────────
  const handleClusterSelect = useCallback((cluster: ICluster) => {
    if (cluster.name === "Kanawa") {
      window.location.href = CLUSTER_REDIRECTS.kanawa || "";
      return;
    }
    // Persist so the modal stays dismissed across ?currentStep changes
    sessionStorage.setItem("selectedCluster", JSON.stringify(cluster));
    setSelectedCluster(cluster);
    setFormData((prev) => ({
      ...prev,
      market_cluster: cluster.name,
      cluster_id: cluster.id,
    }));
  }, []);

  // @ts-ignore
  const handleStepSucess = useCallback((data: any) => {}, []);

  const handleOtpSuccess = useCallback(
    (data: any) => {
      localStorage.setItem("companyData", JSON.stringify(data));
      window.location.href = "/register?currentStep=3";
    },
    [currentStep, setSearchParams],
  );

  // Prevents cluster selection at KYC step
  const isKycContinuationLink =
    currentStep > 1 || Boolean(searchParams.get("verification_code"));

  const SendOTP = () => {
    localStorage.setItem("verificationCode", verificationCode || "");
    const payload = {
      phone_number: phoneNumber,
      verification_code: verificationCode,
      otp_code: otp,
    };
    setLoading(true);
    makeRequest(
      "api/v1/users/otp_verify",
      "post",
      payload,
      handleOtpSuccess,
      console.log,
      () => setLoading(false),
      () => {},
    );
  };

  const saveAndContinueStep1 = useCallback(() => {
    setLoading(true);
    makeRequest(
      REGISTER_USER_URL,
      "POST",
      formData,
      handleStepSucess,
      console.log,
      () => setLoading(false),
    );
  }, [formData]);

  // @ts-ignore
  const handleRedirectToPcn = useCallback((data: any, meta: any) => {
    const PCN_BASE_URL = import.meta.env.VITE_PCN_FRONTEND_BASE_URL;
    const url =
      PCN_BASE_URL +
      "self-service/auth/company-registration?token=" +
      meta?.token;
    setRedirectUrl(url);
    setShowRedirectModal(true);
  }, []);

  const pcnUpdate = useCallback(() => {
    if (pcnData.hasPcn === "yes") {
      let errorMessage = "";
      if (!pcnData.current_pcn_licence_exp_date)
        errorMessage += "Licence expiry date is required. ";
      if (!pcnData.current_pcn_licence_iss_date)
        errorMessage += "Licence issue date is required. ";
      if (!pcnData.pcn_licence_certificate)
        errorMessage += "PCN licence certificate file is required. ";
      if (!pcnData.current_pcn_licence_number)
        errorMessage += "Licence certificate number is required. ";
      if (errorMessage.length > 0) {
        notify({ message: errorMessage, type: "ERROR" });
        return;
      }
    }
    setLoading(true);
    const formDataObj = new FormData();
    (Object.keys(pcnData) as Array<keyof typeof initialPCNDetails>).forEach(
      (key) => {
        if (pcnData[key] && selectedOption?.toLowerCase() === "yes")
          formDataObj.append(key, pcnData[key] as string | Blob);
      },
    );
    formDataObj.append("admin_email", companyData?.email);
    formDataObj.append("onboarding_step", "pcn_licence_stage");
    makeRequest(
      "/api/v1/companies",
      "POST",
      formDataObj,
      handleRedirectToPcn,
      console.log,
      () => setLoading(false),
    );
  }, [pcnData, currentStep, selectedOption]);

  const tinUpdate = useCallback(() => {
    const payload = {
      admin_email: companyData?.email,
      corporate_address: tinData.Address,
      tin: tinData?.TIN,
      rc_number: tinData?.RCNumber,
      date_of_establishment: tinData?.date_of_establishment,
      name: tinData?.TaxPayerName,
      onboarding_step: "tin_verification_stage",
    };
    setLoading(true);
    makeRequest(
      COMPANY_URL,
      "POST",
      payload,
      () => setSearchParams({ currentStep: (currentStep + 1)?.toString() }),
      console.log,
      () => setLoading(false),
    );
  }, [companyData?.email, currentStep, makeRequest, setSearchParams, tinData]);

  const handleNext = () => {
    switch (currentStep) {
      case 1:
        saveAndContinueStep1();
        break;
      case 2:
        SendOTP();
        break;
      case 3:
        tinUpdate();
        break;
      case 4:
        pcnUpdate();
        break;
      default:
        break;
    }
  };

  const handleBack = () => {
    setSearchParams({ currentStep: (currentStep - 1)?.toString() });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev: IStep1Data) => ({ ...prev, [name]: value }));
  };

  const handleChangePCN = (e: any) => {
    const { name, value } = e.target;
    if (name === "pcn_licence_certificate") {
      setPcnData((prev) => ({
        ...prev,
        [name]: (e.target as any)?.files?.[0],
      }));
    } else {
      setPcnData((prev) => ({ ...prev, [name]: value }));
    }
  };

  return (
    <>
      {/* Cluster modal — blocks the page until a cluster is chosen.*/}
      {!selectedCluster && !isKycContinuationLink && (
        <ClusterSelectionModal
          clusters={clustersData ?? []}
          loading={clustersLoading}
          error={clustersError}
          onSelect={handleClusterSelect}
        />
      )}

      <NewRegWrapper>
        <section className="container">
          <section className="info-sec">
            <div className="text-wrapper">
              <h2>
                Getting started is easy.
                <br />
              </h2>
              <p>
                MedEX is a service that simplifies and automates pharmaceutical
                and medical supplies to hospitals, pharmacies, and other
                healthcare providers as well as process prescriptions and
                dispatch medicines to patients.
              </p>
            </div>
            <img className="dots-image" src={dotsImage} alt="" />
          </section>

          <section className="input-sec">
            <FormContainer>
              <Wrapper>
                <SlidderWrapper currentStep={currentStep}>
                  {/* Screen 1 */}
                  <Slide
                    style={{ maxHeight: currentStep === 1 ? "auto" : "2rem" }}
                  >
                    <UserDetail
                      formData={formData}
                      handleChange={handleChange}
                      selectedCluster={selectedCluster}
                      onChangeCluster={() => {
                        sessionStorage.removeItem("selectedCluster");
                        setSelectedCluster(null);
                      }}
                    />
                  </Slide>

                  {/* Screen 2 */}
                  <Slide
                    style={{ maxHeight: currentStep === 2 ? "auto" : "2rem" }}
                  >
                    <OTPVerification
                      phoneNumber={
                        phoneNumber || companyData?.phone_number || ""
                      }
                      setOtp={setOtp}
                      otp={otp}
                    />
                  </Slide>

                  {/* Screen 3 */}
                  <Slide
                    style={{ maxHeight: currentStep === 3 ? "auto" : "2rem" }}
                  >
                    <TinVerification
                      tinData={tinData}
                      setTinData={setTinData}
                    />
                  </Slide>

                  {/* Screen 4 */}
                  <Slide
                    style={{ maxHeight: currentStep === 4 ? "50rem" : "2rem" }}
                  >
                    <PcnIDVerification
                      onChange={handleChangePCN}
                      selectedOption={selectedOption}
                      setSelectedOption={setSelectedOption}
                    />
                  </Slide>
                </SlidderWrapper>

                {currentStep === 1 || currentStep > 4 ? null : (
                  <div className="nav_buttons">
                    {currentStep > 1 && (
                      <IoArrowBackCircleOutline
                        className="back-arrow"
                        onClick={handleBack}
                        size={"40"}
                      />
                    )}
                    <Button loading={loading} onClick={handleNext}>
                      Save and Continue
                    </Button>
                  </div>
                )}
              </Wrapper>
            </FormContainer>
          </section>
        </section>

        <Modal
          title="PCN Self Service Redirect"
          isOpen={showRedirectModal}
          close={() => {
            setShowRedirectModal(false);
            navigate("/login");
          }}
        >
          <RedirectModalWrapper>
            <p>
              You are being redirected to the PCN self service portal for
              registeration. Click on the continue button to continue to PCN
              self service.
            </p>
            <Button onClick={() => window.open(redirectUrl, "_blank")?.focus()}>
              Continue
            </Button>
          </RedirectModalWrapper>
        </Modal>
      </NewRegWrapper>
    </>
  );
}
